import type { GoogleReviewState } from "@/types/CustomerReview";

/**
 * LSP-21829 — the three-state Google review icon (none / sent / rated) shared by every session and project
 * surface: the sessions and projects tables, their cards, the mobile rows and search results.
 */

export const REQUEST_GOOGLE_REVIEW_LABEL = "Request Google Review";
export const GOOGLE_REVIEW_SENT_LABEL = "Google review requested — waiting on a rating";
/** The list payloads carry the state but not the star count, so the rated label cannot name it there. */
export const GOOGLE_REVIEW_RATED_LABEL = "Customer left a rating";

type GoogleReviewStateSource = { googleReviewState?: GoogleReviewState | null; hasGoogleReviewRequest?: boolean | null } | null | undefined;

/**
 * The row's icon state. The server's `googleReviewState` wins; a row from an older API or a search index
 * document that predates the field falls back to the legacy `hasGoogleReviewRequest` flag, which can only
 * ever mean "sent" — a request alone is never a rating.
 */
export function resolveGoogleReviewState(row: GoogleReviewStateSource): GoogleReviewState {
	const state = row?.googleReviewState;
	if (state === "none" || state === "sent" || state === "rated") return state;
	return row?.hasGoogleReviewRequest ? "sent" : "none";
}

/**
 * True when the row's icon carries a badge — a request is out or a rating is in — so the row must read as already
 * requested and must not send another request. `googleReviewState` can be "sent" or "rated" while the legacy
 * `hasGoogleReviewRequest` is false: a project whose request was sent from one of its sessions (the request
 * conversation carries no projectId), or a session whose contact changed after its request was sent. Keying the
 * label and the re-request block off the flag alone would show a badge next to an active "Request Google Review".
 */
export function isGoogleReviewAlreadyRequested(row: GoogleReviewStateSource): boolean {
	return !!row?.hasGoogleReviewRequest || resolveGoogleReviewState(row) !== "none";
}

/** "Customer rated N stars" when the rating is known, otherwise the generic rated label. */
export function googleReviewRatedLabel(rating?: number | null): string {
	if (typeof rating === "number" && Number.isInteger(rating) && rating >= 1) {
		return `Customer rated ${rating} ${rating === 1 ? "star" : "stars"}`;
	}
	return GOOGLE_REVIEW_RATED_LABEL;
}

/**
 * The label for a row whose request is already out (sent or rated). Callers keep their own wording for the
 * states that block a request for another reason (no contact, no permission, loading).
 */
export function googleReviewRequestedLabel(row: GoogleReviewStateSource, rating?: number | null): string {
	return resolveGoogleReviewState(row) === "rated" ? googleReviewRatedLabel(rating) : GOOGLE_REVIEW_SENT_LABEL;
}

/**
 * The optimistic update after a request is sent: the row now has a request out. It never becomes `rated`
 * here — only a customer's rating does that — and a row the server already reported as `rated` (a sibling
 * session of the same contact, for example) keeps its star.
 */
export function markGoogleReviewSent(row: { googleReviewState?: GoogleReviewState | null; hasGoogleReviewRequest?: boolean | null }): void {
	row.hasGoogleReviewRequest = true;
	if (row.googleReviewState !== "rated") row.googleReviewState = "sent";
}
